import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Text } from '@resideo/blueprint-react';
import { InputField, PasswordField } from '@resideo/blueprint-formik';
import { UserTypeEnum } from 'types/enums/User';
import { PasswordValidator } from './PasswordValidator';
import { PhoneInput } from './SignUpForm.styles';
import { CountrySelectorField } from './CountrySelectorField';

interface SignUpFormFieldsProps {
  email?: string;
  isPhonePrePopulated?: boolean;
  userType: UserTypeEnum | undefined;
  showPassword: boolean;
  showPasswordConfirmation: boolean;
  onTogglePassword: () => void;
  onTogglePasswordConfirmation: () => void;
  passwordValidator: {
    lowercase: boolean;
    uppercase: boolean;
    number: boolean;
    symbol: boolean;
    length: boolean;
    match: boolean;
  };
  countryValue?: string;
  phoneCountry?: string;
  onCountryChange: (value: string) => void;
  onCountryBlur: () => void;
  countryError?: string | boolean;
  countryTouched?: boolean;
  passwordConfirmationError?: string | boolean;
  passwordConfirmationTouched?: boolean;
}

const maxCharacterLengthNameFields = 50;
const maxCharacterLengthEmailField = 255;

export const SignUpFormFields: FC<SignUpFormFieldsProps> = ({
  email,
  isPhonePrePopulated,
  userType,
  showPassword,
  showPasswordConfirmation,
  onTogglePassword,
  onTogglePasswordConfirmation,
  passwordValidator,
  countryValue,
  phoneCountry,
  onCountryChange,
  onCountryBlur,
  countryError,
  countryTouched,
  passwordConfirmationError,
  passwordConfirmationTouched,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Text
        textAlign='right'
        data-test-required-field
        fontSize='medium'
        color='#727272'>
        {t('common.requiredFieldsShort')}
      </Text>

      <Box
        flex={1}
        marginBottom='medium'
        data-test-firstname
        className='common-input'>
        <InputField
          name='firstName'
          label={t('common.name.first')}
          required
          version='v1'
          maxLength={maxCharacterLengthNameFields}
        />
      </Box>

      <Box
        flex={1}
        marginBottom='medium'
        data-test-lastname
        className='common-input'>
        <InputField
          name='lastName'
          label={t('common.name.last')}
          required
          version='v1'
          maxLength={maxCharacterLengthNameFields}
        />
      </Box>

      <CountrySelectorField
        value={countryValue}
        onChange={onCountryChange}
        onBlur={onCountryBlur}
        error={countryTouched && countryError}
        disabled={isPhonePrePopulated}
      />

      <Box
        flex={1}
        marginBottom='medium'
        data-test-email
        className='common-input'>
        <InputField
          type='email'
          name='email'
          label={t('common.emailShort')}
          readOnly={!!email}
          disabled={!!email}
          required
          version='v1'
          maxLength={maxCharacterLengthEmailField}
        />
      </Box>

      <Box
        flex={1}
        marginBottom='medium'
        data-test-phone
        className='common-input'>
        <PhoneInput
          type='phoneNumber'
          name='phoneNumber'
          label={t('common.forms.phone')}
          disabled={isPhonePrePopulated}
          required={
            userType === UserTypeEnum.PARTNER ||
            userType === UserTypeEnum.DISTRIBUTOR
          }
          version='v1'
          phoneInputConfig={{
            preferredCountries: ['us', 'ca'],
            country: phoneCountry || 'us',
            countryCodeEditable: false,
          }}
        />
      </Box>

      <Box
        flex={1}
        marginBottom='medium'
        data-test-create-password
        className='common-input'>
        <PasswordField
          label={t('common.password')}
          name='password'
          autoComplete='new-password'
          hidePasswordLabel={t('common.forms.hidePassword')}
          showPasswordLabel={t('common.forms.showPassword')}
          onToggle={onTogglePassword}
          showPassword={showPassword}
          required
          version='v1'
        />
      </Box>

      <PasswordValidator
        lowercase={passwordValidator.lowercase}
        uppercase={passwordValidator.uppercase}
        number={passwordValidator.number}
        symbol={passwordValidator.symbol}
        length={passwordValidator.length}
      />

      <Box
        flex={1}
        marginBottom='medium'
        data-test-create-password-confirmation
        className='common-input'>
        <PasswordField
          label={t('common.confirmPassword')}
          name='passwordConfirmation'
          autoComplete='new-password'
          hidePasswordLabel={t('common.forms.hidePassword')}
          showPasswordLabel={t('common.forms.showPassword')}
          onToggle={onTogglePasswordConfirmation}
          showPassword={showPasswordConfirmation}
          required
          version='v1'
          error={passwordConfirmationTouched && passwordConfirmationError}
        />
      </Box>
    </>
  );
};
